import { useMemo } from 'react';
import { Dropdown, Button, Typography, Space, message, Grid } from 'antd';
import { ShopOutlined, DownOutlined, CheckOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { authService } from '@/services/auth.service';
import { setCredentials } from '@/redux/slices/authSlice';
import { setPosShiftStoreId, usePosShiftStoreId } from '@/utils/posShift';
import type { RootState } from '@/redux';

const { Text } = Typography;
const { useBreakpoint } = Grid;

/**
 * Store selector dropdown (EG-355 §4). Hiện store user đang làm việc; click
 * dropdown để switch sang store khác. Cashier có shift lock — chuyển store
 * = exchangeStoreToken (issue token mới với JWT.sid mới). Owner/manager đa
 * store mặc định (không cần lock); UI vẫn cho switch để filter view.
 *
 * Hide nếu user chỉ có 1 store.
 */
export const StoreSelector = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector((s: RootState) => s.auth.user);
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const { data: stores = [], isLoading } = useQuery({
    queryKey: ['auth', 'my-stores'],
    queryFn: () => authService.listMyStores(),
    staleTime: 60_000,
    retry: 0,
  });

  const exchangeMutation = useMutation({
    mutationFn: (storeId: string) => authService.exchangeStoreToken(storeId),
    onSuccess: (tokens, storeId) => {
      if (!user) return;
      dispatch(
        setCredentials({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          user: { ...user, storeId },
        }),
      );
      // Sync sessionStorage để POS axios interceptor + sidebar dropdown đọc đúng store.
      setPosShiftStoreId(storeId);
      // Invalidate POS queries để re-fetch theo store mới.
      void queryClient.invalidateQueries({ queryKey: ['pos'] });
      void queryClient.invalidateQueries({ queryKey: ['pos-variant-search'] });
      const target = stores.find((s) => s.storeId === storeId);
      message.success(`Đã chuyển sang ${target?.name ?? 'cửa hàng'}`);
    },
    onError: () => message.error('Không chuyển được cửa hàng'),
  });

  // Active store source-of-truth: sessionStorage.pos.shiftStoreId (sync với POS
  // sidebar dropdown). Fallback user.storeId nếu sessionStorage trống (cashier
  // chưa mở ca / hook chưa được wire trên page non-POS).
  const shiftStoreId = usePosShiftStoreId();
  const currentStore = useMemo(() => {
    if (shiftStoreId) {
      const fromShift = stores.find((s) => s.storeId === shiftStoreId);
      if (fromShift) return fromShift;
    }
    return stores.find((s) => s.storeId === user?.storeId) ?? stores[0];
  }, [stores, user?.storeId, shiftStoreId]);

  if (isLoading || stores.length <= 1) {
    if (!currentStore) return null;
    if (isMobile) {
      return <ShopOutlined style={{ color: '#a4193d', fontSize: 18 }} />;
    }
    return (
      <Space size={6}>
        <ShopOutlined style={{ color: '#a4193d' }} />
        <Text strong style={{ fontSize: 13 }}>
          {currentStore.name}
        </Text>
      </Space>
    );
  }

  const items = stores.map((s) => ({
    key: s.storeId,
    label: (
      <Space>
        {s.storeId === currentStore?.storeId && (
          <CheckOutlined style={{ color: '#a4193d' }} />
        )}
        <span>{s.name}</span>
      </Space>
    ),
  }));

  return (
    <Dropdown
      menu={{
        items,
        onClick: ({ key }) => {
          if (key === currentStore?.storeId) return;
          // Cashier (có user.storeId — sid lock from JWT) → exchangeStoreToken
          // để issue token mới với shift_store_id mới. Owner/manager (không sid)
          // → chỉ setPosShiftStoreId + invalidate cache, không cần đổi token.
          if (user?.storeId) {
            exchangeMutation.mutate(key);
          } else {
            setPosShiftStoreId(key);
            void queryClient.invalidateQueries({ queryKey: ['pos'] });
            void queryClient.invalidateQueries({ queryKey: ['pos-variant-search'] });
            const target = stores.find((s) => s.storeId === key);
            message.success(`Đã chuyển sang ${target?.name ?? 'cửa hàng'}`);
          }
        },
      }}
      trigger={['click']}
      disabled={exchangeMutation.isPending}
    >
      <Button type="text" size="small" loading={exchangeMutation.isPending}>
        <Space size={6}>
          <ShopOutlined style={{ color: '#a4193d' }} />
          {!isMobile && (
            <Text strong style={{ fontSize: 13 }}>
              {currentStore?.name ?? 'Chọn cửa hàng'}
            </Text>
          )}
          <DownOutlined style={{ fontSize: 10 }} />
        </Space>
      </Button>
    </Dropdown>
  );
};
